import { themeAtom } from "../../shared/atoms";
import Datepicker from "../../shared/components/Datepicker/Datepicker";
import LoadingWrapper from "../../shared/components/LoadingWrapper";
import { ports } from "../../shared/const/ports.const";
import { api, apiUrl } from "../../shared/services/service";
import { piClubs } from "../const/pi-clubs.const";
import {
  Bq10MiscellaneousM,
  initlaBq10Miscellaneous,
  mapSqlToBq10MiscellaneousForm,
} from "../models/bq10-miscellaneous.model";
import { BqCommVis } from "../models/bq-forms.models";
import { bqF10MiscellaneousAtom } from "../state/baltic-form.atoms";
import { InputCell, formDate } from "../services/baltic-forms.utils";
import { useAtom, useAtomValue } from "jotai";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router";
import { twMerge } from "tailwind-merge";

interface Props {
  isCompletedForm: boolean;
  cacheInitialForm: (form: any) => void;
  readonly: boolean;
}

export const Bq10Miscellaneous: React.FC<Props> = ({ readonly, isCompletedForm, cacheInitialForm }) => {
  const theme = useAtomValue(themeAtom);
  const [storedForm, setStoredForm] = useAtom(bqF10MiscellaneousAtom);

  const { imoNumber, vesselName } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [form, setForm] = useState<Bq10MiscellaneousM>(initlaBq10Miscellaneous);
  const [commentsVisible, setCommentsVisible] = useState<BqCommVis>({});

  function toggleComments(inputId: string) {
    const newComms = { ...commentsVisible };
    newComms[inputId] = !commentsVisible[inputId];

    setCommentsVisible(newComms);
  }

  function isCommentsVisible(inputId: string): boolean {
    return commentsVisible[inputId];
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    if (type === "checkbox") {
      setForm((prev) => ({ ...prev, [name]: (e.target as HTMLInputElement).checked }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
  };

  // Get
  useEffect(() => {
    const _form = storedForm ?? {};
    const storageisEmpty = Object.keys(_form).length === 0;

    if (isCompletedForm) {
      fetchData();
    } else {
      if (storageisEmpty) {
        fetchData();
      } else {
        setForm(_form);
        cacheInitialForm(_form);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCompletedForm]);

  // Storage
  useEffect(() => {
    if (!isLoading) {
      setStoredForm(form);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const result = await api.get(`${apiUrl}/bq_Miscellaneous`, {
        params: {
          imo_no: imoNumber,
          vessel_name: vesselName,
        },
      });
      const _formSql = result.data[0];
      const _form = mapSqlToBq10MiscellaneousForm(_formSql);
      setForm(_form);
      setStoredForm(_form);
      cacheInitialForm(_form);
    } catch (error: unknown) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const updateDate = (date: Date | null, field: string) => {
    setForm((prevState) => {
      const _form = {
        ...prevState,
        [field]: date !== null ? date.toISOString() : null,
      };

      return _form;
    });
  };

  return (
    <LoadingWrapper isLoading={isLoading} loadingIndicatorSize="lg" loadingIndicatorClassName="text-green">
      <div
        className={twMerge(
          "p-2 md:p-4",
          "[&_input]:border",
          "[&_select]:text-sm",
          theme.home.tailwind.panelBackground,
          theme.home.tailwind.textDefault,
          theme.balticQuestionnaire.labelsText,
          theme.balticQuestionnaire.inputsBg,
          theme.balticQuestionnaire.inputsText,
          theme.balticQuestionnaire.inputsBorder,
          theme.balticQuestionnaire.inputsFocusBorder,
          theme.balticQuestionnaire.inputsFocusRing,
          theme.balticQuestionnaire.selectsFocusBorder,
          theme.balticQuestionnaire.selectsFocusRing,
          theme.balticQuestionnaire.selectsBg,
          theme.balticQuestionnaire.selectsBorder,
          theme.balticQuestionnaire.textsBg,
          theme.balticQuestionnaire.textsText,
          theme.balticQuestionnaire.textsBorder,
          theme.balticQuestionnaire.selectsScroller,
          theme.balticQuestionnaire.textAreasFocusBorder,
          theme.balticQuestionnaire.textAreasFocusRing,
        )}
      >
        <h1 className="custom-heading mb-4">Miscellaneous</h1>

        <form onSubmit={handleSubmit}>
          {/* Constants/Fresh Water */}
          <h2 className="mb-2 text-lg font-semibold">Constants / Fresh Water</h2>

          <div className="mb-4 grid grid-cols-4 gap-4">
            <div className="col-span-1">
              <label className="block text-sm font-bold">Constant excluding fresh water</label>
              <InputCell
                disabled={readonly}
                name="constantExcludingFreshWater"
                value={form.constantExcludingFreshWater}
                onChange={handleChange}
                trailingText="Metric Tonnes"
                inputType="number"
              />
              <label className="block text-sm font-bold">
                <button
                  className={theme.balticQuestionnaire.blueText}
                  onClick={() => toggleComments("constantExcludingFreshWaterComments")}
                >
                  Comments
                </button>
              </label>
              {(form.constantExcludingFreshWaterComments ||
                isCommentsVisible("constantExcludingFreshWaterComments")) && (
                <textarea
                  disabled={readonly}
                  name="constantExcludingFreshWaterComments"
                  value={form.constantExcludingFreshWaterComments}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
                />
              )}
            </div>
          </div>

          <div className="mb-4 grid grid-cols-4 gap-4">
            <div className="col-span-1">
              <label className="block text-sm font-bold">Domestic fresh water consumption</label>
              <InputCell
                disabled={readonly}
                name="domesticFreshWaterConsumption"
                value={form.domesticFreshWaterConsumption}
                onChange={handleChange}
                trailingText="MT/Day"
                inputType="number"
              />
            </div>
          </div>

          <div className="mb-4 grid grid-cols-4 gap-4">
            <div className="col-span-1">
              <label className="block text-sm font-bold">Storage capacity fresh water</label>
              <InputCell
                disabled={readonly}
                name="storageCapacityFreshWater"
                value={form.storageCapacityFreshWater}
                onChange={handleChange}
                trailingText="Cu. Metres"
                inputType="number"
              />
            </div>
          </div>

          <div className="mb-4 grid grid-cols-4 gap-4">
            <div className="col-span-1">
              <label className="block text-sm font-bold">Daily production of fresh water evaporator</label>
              <InputCell
                disabled={readonly}
                name="fwEvaporatorDailyProduction"
                value={form.fwEvaporatorDailyProduction}
                onChange={handleChange}
                trailingText="MT/Day"
                inputType="number"
              />
            </div>
          </div>

          <div className="mb-4 grid grid-cols-4 gap-4">
            <div className="col-span-1">
              <label className="block text-sm font-bold">State capacity of fresh water evaporator</label>
              <InputCell
                disabled={readonly}
                name="fwEvaporatorCapacity"
                value={form.fwEvaporatorCapacity}
                onChange={handleChange}
                trailingText="Metric Tonnes"
                inputType="number"
              />
            </div>
          </div>

          <div className="mb-4 grid grid-cols-4 gap-4">
            <div className="col-span-1">
              <label className="block text-sm font-bold">Normal fresh water reserve</label>
              <InputCell
                disabled={readonly}
                name="normalFreshWaterReserve"
                value={form.normalFreshWaterReserve}
                onChange={handleChange}
                trailingText="Metric Tonnes"
                inputType="number"
              />
            </div>
          </div>

          {/* Insurance */}
          <h2 className="mb-2 mt-[120px] text-lg font-semibold">Insurance</h2>

          {/* P&I */}
          <label className="mb-2 block text-sm font-bold">Certificate of Insurance (P&I)</label>

          <div className="mb-4 grid grid-cols-4 gap-4">
            <div className="col-span-1">
              <label className="block text-xs font-bold">Name of P&I Club</label>
              <select
                disabled={readonly}
                name="pAndIClubName"
                value={form.pAndIClubName}
                onChange={handleChange}
                className="mt-1 w-full rounded-md border p-2 text-sm focus:outline-none focus:ring"
              >
                <option value=""></option>
                {piClubs.map((piClub) => (
                  <option key={piClub} value={piClub}>
                    {piClub}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-span-1">
              <label className="block text-xs font-bold">P&I Club Address</label>
              <textarea
                disabled={readonly}
                name="pAndIClubAddress"
                value={form.pAndIClubAddress}
                onChange={handleChange}
                rows={2}
                className="mt-1 w-full rounded-md border p-2 text-sm focus:outline-none focus:ring"
              />
            </div>
          </div>

          <div className="mb-4 grid grid-cols-4 gap-4">
            <div className="col-span-1">
              <label className="block text-xs font-bold">P&I Club Telephone</label>
              <input
                disabled={readonly}
                type="text"
                name="pAndIClubTelephone"
                value={form.pAndIClubTelephone}
                onChange={handleChange}
                className="mt-1 w-full rounded-md border p-2 text-sm focus:outline-none focus:ring"
              />
            </div>

            <div className="col-span-1">
              <label className="block text-xs font-bold">P&I Club Fax</label>
              <input
                disabled={readonly}
                type="text"
                name="pAndIClubFax"
                value={form.pAndIClubFax}
                onChange={handleChange}
                className="mt-1 w-full rounded-md border p-2 text-sm focus:outline-none focus:ring"
              />
            </div>

            <div className="col-span-1">
              <label className="block text-xs font-bold">P&I Club Telex</label>
              <input
                disabled={readonly}
                type="text"
                name="pAndIClubTelex"
                value={form.pAndIClubTelex}
                onChange={handleChange}
                className="mt-1 w-full rounded-md border p-2 text-sm focus:outline-none focus:ring"
              />
            </div>

            <div className="col-span-1">
              <label className="block text-xs font-bold">P&I Club Email</label>
              <input
                disabled={readonly}
                type="text"
                name="pAndIClubEmail"
                value={form.pAndIClubEmail}
                onChange={handleChange}
                className="mt-1 w-full rounded-md border p-2 text-sm focus:outline-none focus:ring"
              />
            </div>

            <div className="col-span-1">
              <label className="block text-xs font-bold">P&I Club Website</label>
              <input
                disabled={readonly}
                type="text"
                name="pAndIClubWebsite"
                value={form.pAndIClubWebsite}
                onChange={handleChange}
                className="mt-1 w-full rounded-md border p-2 text-sm focus:outline-none focus:ring"
              />
            </div>
          </div>

          <div className="mb-4 grid grid-cols-4 gap-4">
            <div className="col-span-1">
              <label className="block text-sm font-bold">
                Certificate of Insurance (P&I) (pollution liability coverage)
              </label>
              <InputCell
                disabled={readonly}
                name="pAndIPollutionLiabilityCoverage"
                value={form.pAndIPollutionLiabilityCoverage}
                onChange={handleChange}
                trailingText="US$"
                inputType="number"
              />
              <label className="block text-sm font-bold">
                <button
                  className={theme.balticQuestionnaire.blueText}
                  onClick={() => toggleComments("pAndIPollutionLiabilityCoverageComments")}
                >
                  Comments
                </button>
              </label>
              {(form.pAndIPollutionLiabilityCoverageComments ||
                isCommentsVisible("pAndIPollutionLiabilityCoverageComments")) && (
                <textarea
                  disabled={readonly}
                  name="pAndIPollutionLiabilityCoverageComments"
                  value={form.pAndIPollutionLiabilityCoverageComments}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
                />
              )}
            </div>
          </div>

          <div className="mb-4 grid grid-cols-4 gap-4">
            <div className="col-span-1">
              <label className="block text-sm font-bold">Certificate of Insurance (H&M) (Insurer)</label>
              <input
                disabled={readonly}
                type="text"
                name="handmInsurer"
                value={form.handmInsurer}
                onChange={handleChange}
                className={twMerge("mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring")}
              />
            </div>
          </div>

          <div className="mb-4 grid grid-cols-4 gap-4">
            <div className="col-span-1">
              <label className="block text-sm font-bold">Cert. of Insurance (H&M) (insured value)</label>
              <InputCell
                disabled={readonly}
                name="handmInsuredValue"
                value={form.handmInsuredValue}
                onChange={handleChange}
                trailingText="US$"
                inputType="number"
              />
            </div>
          </div>

          {/* Vetting */}
          <h2 className="mb-2 mt-[120px] text-lg font-semibold">Vetting</h2>

          <div className="mb-4 grid grid-cols-4 gap-4">
            <div className="col-span-1">
              <label className="block text-sm font-bold">Is the vessel RIGHTSHIP approved?</label>
              <div className="mt-1 flex space-x-4">
                <label className="inline-flex items-center">
                  <input
                    disabled={readonly}
                    type="radio"
                    name="isRightShipApproved"
                    value="yes"
                    checked={form.isRightShipApproved === "yes"}
                    onChange={handleChange}
                    className="form-radio"
                  />
                  <span className="ml-1">Yes</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    disabled={readonly}
                    type="radio"
                    name="isRightShipApproved"
                    value="no"
                    checked={form.isRightShipApproved === "no"}
                    onChange={handleChange}
                    className="form-radio"
                  />
                  <span className="ml-1">No</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    disabled={readonly}
                    type="radio"
                    name="isRightShipApproved"
                    value="n/a"
                    checked={form.isRightShipApproved === "n/a"}
                    onChange={handleChange}
                    className="form-radio"
                  />
                  <span className="ml-1">N/A</span>
                </label>
              </div>
              <label className="block text-sm font-bold">
                <button
                  className={theme.balticQuestionnaire.blueText}
                  onClick={() => toggleComments("isRightShipApprovedComments")}
                >
                  Comments
                </button>
              </label>
              {(form.isRightShipApprovedComments || isCommentsVisible("isRightShipApprovedComments")) && (
                <textarea
                  disabled={readonly}
                  name="isRightShipApprovedComments"
                  value={form.isRightShipApprovedComments}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
                />
              )}
            </div>
          </div>

          <div className="mb-4 grid grid-cols-4 gap-4">
            <div className="col-span-1">
              <label className="block text-sm font-bold">Date of last RIGHTSHIP inspection</label>
              <Datepicker
                disabled={readonly}
                selected={formDate(form.lastRightShipInspectionDate ?? "")}
                onChange={(date) => updateDate(date, "lastRightShipInspectionDate")}
              />
            </div>

            <div className="col-span-1">
              <label className="block text-sm font-bold">Place of last RIGHTSHIP inspection</label>
              <input
                disabled={readonly}
                type="text"
                name="lastRightShipInspectionPlace"
                value={form.lastRightShipInspectionPlace}
                onChange={handleChange}
                className={twMerge("mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring")}
              />
            </div>
          </div>

          <div className="mb-4 grid grid-cols-4 gap-4">
            <div className="col-span-1">
              <label className="block text-sm font-bold">Date of last IDWAL inspection</label>
              <Datepicker
                disabled={readonly}
                selected={formDate(form.lastIDWALInspectionDate ?? "")}
                onChange={(date) => updateDate(date, "lastIDWALInspectionDate")}
              />
            </div>

            <div className="col-span-1">
              <label className="block text-sm font-bold">Place of last IDWAL inspection</label>
              <input
                disabled={readonly}
                type="text"
                name="lastIDWALInspectionPlace"
                value={form.lastIDWALInspectionPlace}
                onChange={handleChange}
                className={twMerge("mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring")}
              />
            </div>
          </div>

          {/* Port State Control */}
          <h2 className="mb-2 mt-[120px] text-lg font-semibold">Port State Control</h2>

          <div className="mb-4 grid grid-cols-4 gap-4">
            <div className="col-span-1">
              <label className="block text-sm font-bold">Date of last Port State Control Inspection:</label>
              <Datepicker
                disabled={readonly}
                selected={formDate(form.lastPSCDate ?? "")}
                onChange={(date) => updateDate(date, "lastPSCDate")}
              />
              <label className="block text-sm font-bold">
                <button
                  className={theme.balticQuestionnaire.blueText}
                  onClick={() => toggleComments("lastPSCDateComments")}
                >
                  Comments
                </button>
              </label>
              {(form.lastPSCDateComments || isCommentsVisible("lastPSCDateComments")) && (
                <textarea
                  disabled={readonly}
                  name="lastPSCDateComments"
                  value={form.lastPSCDateComments}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
                />
              )}
            </div>
          </div>

          <div className="mb-4 grid grid-cols-4 gap-4">
            <div className="col-span-1">
              <label className="block text-sm font-bold">Port of last Port State Control Inspection:</label>
              <select
                disabled={readonly}
                name="lastPSCPort"
                value={form.lastPSCPort}
                onChange={handleChange}
                className={twMerge("mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring")}
              >
                <option value=""></option>
                {ports.map((port) => (
                  <option key={port} value={port}>
                    {port}
                  </option>
                ))}
              </select>
              <label className="block text-sm font-bold">
                <button
                  className={theme.balticQuestionnaire.blueText}
                  onClick={() => toggleComments("lastPSCPortComments")}
                >
                  Comments
                </button>
              </label>
              {(form.lastPSCPortComments || isCommentsVisible("lastPSCPortComments")) && (
                <textarea
                  disabled={readonly}
                  name="lastPSCPortComments"
                  value={form.lastPSCPortComments}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
                />
              )}
            </div>
          </div>

          <div className="mb-4 grid grid-cols-4 gap-4">
            <div className="col-span-1">
              <label className="block text-sm font-bold">
                Any outstanding deficiencies as reported by Port State Control (PSC)
              </label>
              <div className="mt-1 flex space-x-4">
                <label className="inline-flex items-center">
                  <input
                    disabled={readonly}
                    type="radio"
                    name="outstandingDeficienciesPSC"
                    value="yes"
                    checked={form.outstandingDeficienciesPSC === "yes"}
                    onChange={handleChange}
                    className="form-radio"
                  />
                  <span className="ml-1">Yes</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    disabled={readonly}
                    type="radio"
                    name="outstandingDeficienciesPSC"
                    value="no"
                    checked={form.outstandingDeficienciesPSC === "no"}
                    onChange={handleChange}
                    className="form-radio"
                  />
                  <span className="ml-1">No</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    disabled={readonly}
                    type="radio"
                    name="outstandingDeficienciesPSC"
                    value="n/a"
                    checked={form.outstandingDeficienciesPSC === "n/a"}
                    onChange={handleChange}
                    className="form-radio"
                  />
                  <span className="ml-1">N/A</span>
                </label>
              </div>
              <label className="block text-sm font-bold">
                <button
                  className={theme.balticQuestionnaire.blueText}
                  onClick={() => toggleComments("outstandingDeficienciesPSCComments")}
                >
                  Comments
                </button>
              </label>
              {(form.outstandingDeficienciesPSCComments || isCommentsVisible("outstandingDeficienciesPSCComments")) && (
                <textarea
                  disabled={readonly}
                  name="outstandingDeficienciesPSCComments"
                  value={form.outstandingDeficienciesPSCComments}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
                />
              )}
            </div>
          </div>

          <div className="mb-4 grid grid-cols-4 gap-4">
            <div className="col-span-1">
              <label className="mt-2 block text-sm font-bold">
                Please provide details of outstanding deficiencies as reported by Port State Control (PSC):
              </label>
              <textarea
                disabled={readonly}
                name="deficienciesPSCDetails"
                value={form.deficienciesPSCDetails}
                onChange={handleChange}
                rows={3}
                className={twMerge("mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring")}
              />
            </div>
          </div>

          <div className="mb-4 grid grid-cols-4 gap-4">
            <div className="col-span-1">
              <label className="block text-sm font-bold">Vessels current GHG in Rightship:</label>
              <input
                disabled={readonly}
                type="text"
                name="vesselGHGRightShip"
                value={form.vesselGHGRightShip}
                onChange={handleChange}
                className={twMerge("mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring")}
              />
            </div>
          </div>

          <div className="mb-4 grid grid-cols-4 gap-4">
            <div className="col-span-1">
              <label className="block text-sm font-bold">
                Has any of the above entities in the ownership and operation sections currently or have been sanctioned
                by USA/EU or other national entity in the last 5 years:
              </label>
              <div className="mt-1 flex space-x-4">
                <label className="inline-flex items-center">
                  <input
                    disabled={readonly}
                    type="radio"
                    name="anySanctions"
                    value="yes"
                    checked={form.anySanctions === "yes"}
                    onChange={handleChange}
                    className="form-radio"
                  />
                  <span className="ml-1">Yes</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    disabled={readonly}
                    type="radio"
                    name="anySanctions"
                    value="no"
                    checked={form.anySanctions === "no"}
                    onChange={handleChange}
                    className="form-radio"
                  />
                  <span className="ml-1">No</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    disabled={readonly}
                    type="radio"
                    name="anySanctions"
                    value="n/a"
                    checked={form.anySanctions === "n/a"}
                    onChange={handleChange}
                    className="form-radio"
                  />
                  <span className="ml-1">N/A</span>
                </label>
              </div>
            </div>
          </div>

          <div className="mb-4 grid grid-cols-4 gap-4">
            <div className="col-span-1">
              <label className="mt-2 block text-sm font-bold">
                If any of the above entities in the ownership and operation sections currently or have been sanctioned
                by USA/EU or other national entity in the last 5 years, please provide details:
              </label>
              <textarea
                disabled={readonly}
                name="anySanctionsDetails"
                value={form.anySanctionsDetails}
                onChange={handleChange}
                rows={3}
                className={twMerge("mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring")}
              />
            </div>
          </div>

          <div className="mb-4 grid grid-cols-4 gap-4">
            <div className="col-span-1">
              <label className="block text-sm font-bold">
                Any Australian Maritime Safety Authority (AMSA) detentions or noted deficiencies. If so, please advise
                details and specify when/where these items were repaired:
              </label>
              <textarea
                disabled={readonly}
                name="amsaDetentions"
                value={form.amsaDetentions}
                onChange={handleChange}
                rows={2}
                className={twMerge("mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring")}
              />
            </div>
          </div>

          <h2 className="mb-2 mt-[120px] text-lg font-semibold">
            Supplementary information for specific commodities / trades
          </h2>

          <div className="mb-4 grid grid-cols-4 gap-4">
            <div className="col-span-1">
              <label className="block text-sm font-bold">
                Supplementary information for specific commodities/trades:
              </label>
              <textarea
                disabled={readonly}
                name="supplementaryCommoditiesInfo"
                value={form.supplementaryCommoditiesInfo}
                onChange={handleChange}
                rows={3}
                className={twMerge("mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring")}
              />
            </div>
          </div>

          {/* Return email copy */}
          <div className="mb-4">
            <label className="mt-2 inline-flex items-center">
              <input
                disabled={readonly}
                type="checkbox"
                name="sendByReturnEmail"
                checked={form.sendByReturnEmail}
                onChange={handleChange}
                className="form-checkbox text-green"
              />
              <span className="ml-2 text-sm">
                Click here and a copy of the completed questionnaire will be sent to you by return email:
              </span>
            </label>
          </div>

          <br />
        </form>
      </div>
    </LoadingWrapper>
  );
};
